import { Card } from '../primitives/Card'
import { Badge } from '../primitives/Badge'
import { Icon } from '../primitives/Icon'
import { formatDays, toNumber } from '../../lib/format'
import {
  FULL_MONTH_DAY_THRESHOLD,
  MONTH_LABELS,
  hrRound,
  joinMonthCredit,
  parseUtcDate,
} from './_accrualUtils'

export interface AccrualBarProps {
  /** HR-set full annual grant in days (e.g. 14, 18, 21). The bar
   *  derives the yearly slice + per-month drip rate from this. */
  entitlement: number
  /** ISO date `YYYY-MM-DD` the employee joined. */
  joinDate: string
  /** Optional override for "now"; tests pass a fixed date for snapshot
   *  determinism. */
  now?: Date
}

interface JoiningState {
  perMonth: number
  totalAccrued: number
  yearlySlice: number
  firstAccruedIdx: number
  currentMonthIdx: number
  /** Cumulative accrued days at end of each month. `null` for pre-join
   *  cells in the join year. HR-rounded so cell values line up with the
   *  badge total. */
  cellValues: ReadonlyArray<number | null>
}

function compute(props: AccrualBarProps): JoiningState | null {
  const now = props.now ?? new Date()
  const join = parseUtcDate(props.joinDate)
  const entitlement = toNumber(props.entitlement)
  const displayYear = now.getUTCFullYear()
  const currentMonthIdx = now.getUTCMonth()
  const joinYear = join.getUTCFullYear()
  const joinMonthIdx = join.getUTCMonth()

  // Only render in the first 12 months of tenure — past the anniversary
  // the bar adds no info beyond the BalancesCard's full grant.
  const monthsSinceJoin =
    (displayYear - joinYear) * 12 + (currentMonthIdx - joinMonthIdx)
  if (monthsSinceJoin < 0 || monthsSinceJoin >= 12) return null

  const sameYear = joinYear === displayYear
  const firstAccruedIdx = sameYear ? joinMonthIdx : 0
  // §1.2: only the join month is haircut by the day-15 rule. Cross-year
  // (Pre-Anniversary) drips from January at the full monthly rate.
  const fullMonthsAfterJoin = Math.max(0, currentMonthIdx - firstAccruedIdx)
  const monthsAccrued = sameYear
    ? joinMonthCredit(join) + fullMonthsAfterJoin
    : fullMonthsAfterJoin + 1
  // Policy §1.2 Option C: drip rate is constant — `full_grant / 12` for
  // every employee at this grant tier, regardless of join month. The
  // yearly slice (HR-rounded `full × (13 - join_month) / 12`) acts as
  // the badge denominator + an upper cap on what cells can show; it
  // does NOT alter the per-month rate.
  const perMonth = entitlement > 0 ? entitlement / 12 : 0
  const yearlySlice = sameYear
    ? hrRound((entitlement * (13 - (joinMonthIdx + 1))) / 12)
    : entitlement
  // Cumulative accrued at end of each month. null for pre-join cells
  // AND future cells (showing a forecasted number on Aug when it's only
  // May reads like "you already have 8 days" — misleading). Capped at
  // the yearly slice so a rounding wobble never overshoots.
  const cellValues: ReadonlyArray<number | null> = MONTH_LABELS.map(
    (_label, idx) => {
      if (idx < firstAccruedIdx) return null
      if (idx > currentMonthIdx) return null
      const monthsAt = sameYear
        ? joinMonthCredit(join) + Math.max(0, idx - firstAccruedIdx)
        : idx + 1
      return Math.min(yearlySlice, hrRound(perMonth * monthsAt))
    },
  )
  return {
    perMonth,
    totalAccrued: hrRound(perMonth * monthsAccrued),
    yearlySlice,
    firstAccruedIdx,
    currentMonthIdx,
    cellValues,
  }
}

/**
 * 12-month accrual visualisation for first-year employees only.
 *
 * Renders nothing once the employee is past their 12-month anniversary —
 * tenured employees with a full grant have no monthly drip to visualise
 * and the BalancesCard already shows the full number.
 *
 * For leaving-mode (employee has a `last_working_day` or
 * `contract_end_date`) use `ProRatedBar` instead.
 */
export function AccrualBar(props: AccrualBarProps): JSX.Element | null {
  const state = compute(props)
  if (state === null) return null

  return (
    <Card padding={16} style={{ marginTop: 16 }} data-testid="accrual-bar">
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          <Icon name="trending_up" size={16} aria-hidden="true" />
          <span>Annual leave earned by month</span>
          <Badge variant="info">
            {formatDays(state.totalAccrued)}/{formatDays(state.yearlySlice)}
          </Badge>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          Each cell shows the total days you&apos;ve earned by the end of
          that month. Builds up to {formatDays(state.yearlySlice)} for the
          year.
        </span>
      </header>

      <div
        data-testid="accrual-month-grid"
        data-mode="joining"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          gap: 4,
        }}
      >
        {MONTH_LABELS.map((label, idx) => {
          const isAccrued = idx >= state.firstAccruedIdx && idx <= state.currentMonthIdx
          const isCurrent = idx === state.currentMonthIdx
          const value = state.cellValues[idx]
          return (
            <div
              key={label}
              data-testid="accrual-month-cell"
              data-month={label}
              data-accrued={isAccrued ? 'true' : 'false'}
              data-current={isCurrent ? 'true' : 'false'}
              data-lost="false"
              style={{ textAlign: 'center' }}
            >
              <div
                style={{
                  height: 28,
                  borderRadius: 6,
                  border: isCurrent
                    ? '2px solid var(--steel-blue)'
                    : '1px solid var(--french-gray-2)',
                  background: isAccrued
                    ? isCurrent
                      ? 'var(--steel-blue)'
                      : 'var(--primary-bg)'
                    : 'var(--surface-2)',
                  color: isAccrued && isCurrent
                    ? 'var(--surface)'
                    : 'var(--text-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {value === null || value === undefined ? '—' : formatDays(value)}
              </div>
              <div
                style={{
                  fontSize: 9,
                  marginTop: 3,
                  color: isCurrent ? 'var(--steel-blue)' : 'var(--text-3)',
                  fontWeight: isCurrent ? 700 : 500,
                }}
              >
                {label}
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

// Re-export for tests / external consumers that previously read the
// threshold from this module.
export { FULL_MONTH_DAY_THRESHOLD }
